<template>
	<FormContainer
		class="notification-settings"
		title="Notifications">
		<form
			class="notification-settings__form"
			@submit.prevent="submit">
			<section class="notification-settings__section">
				<div class="notification-settings__copy">
					<p class="notification-settings__eyebrow">Email Preferences</p>
					<h2 class="notification-settings__title">Monthly token emails</h2>
					<p class="notification-settings__description">
						Get an email when your monthly free token batch is added to your account.
					</p>
				</div>

				<div class="notification-settings__toggle-row">
					<label
						class="notification-settings__toggle-label"
						for="monthly-token-notifications">
						Email me when monthly tokens are added
					</label>
					<ToggleSwitch
						input-id="monthly-token-notifications"
						v-model="formData.receives_monthly_token_notifications"
						:disabled="loading" />
				</div>
			</section>

			<p
				v-if="saveError"
				class="notification-settings__error"
				role="alert">
				{{ saveError }}
			</p>

			<div class="notification-settings__footer">
				<p
					class="notification-settings__status"
					aria-live="polite">
					{{ statusMessage }}
				</p>
				<Button
					label="Save preferences"
					icon="fal fa-save"
					type="submit"
					:disabled="!isDirty"
					:loading="loading" />
			</div>
		</form>
	</FormContainer>
</template>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import Button from 'primevue/button'
import ToggleSwitch from 'primevue/toggleswitch'
import { useToast } from 'primevue/usetoast'
import FormContainer from '@/components/FormContainer.vue'
import UsersApi from '@/models/User/Api'
import type { User } from '@/models/User/Model'
import { useForm } from '@/helpers/form'
import { useAuthStore } from '@/stores/Auth'

type FormData = {
	receives_monthly_token_notifications: boolean
}

const props = defineProps<{
	userId: User['id']
}>()

const authStore = useAuthStore()
const toast = useToast()
const saveError = ref('')

const { formData, isDirty, loading, submit } = useForm({
	api: () => new UsersApi(),
	defaultData: (): FormData => ({
		receives_monthly_token_notifications: true,
	}),
	id: toRef(props, 'userId'),
	redirectProtection: () => false,
	onSubmitError: () => {
		saveError.value = 'Could not save preferences. Please try again.'
	},
	onUpdated: async () => {
		saveError.value = ''
		if (props.userId === authStore.user?.id) await authStore.getUser()
		toast.add({
			severity: 'success',
			summary: 'Preferences saved',
			detail: 'Monthly token notification settings updated.',
			life: 3000,
		})
	},
})

const statusMessage = computed(() => {
	if (isDirty.value) return 'You have unsaved changes. Save preferences to apply them.'

	return formData.value.receives_monthly_token_notifications
		? 'You will receive an email each month when new free tokens are credited.'
		: 'Monthly token emails are off. You will still receive the tokens automatically.'
})
</script>

<style scoped lang="scss">
.notification-settings {
	max-width: unset;

	.notification-settings__form {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.notification-settings__section {
		display: flex;
		flex-direction: column;
		gap: 20px;
		padding: 20px;
		border: 1px solid var(--p-surface-700);
		border-radius: 18px;
		background:
			radial-gradient(circle at top left, rgba(238, 255, 1, 0.12), transparent 42%),
			linear-gradient(180deg, var(--p-surface-900), var(--p-surface-950));
	}

	.notification-settings__copy {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.notification-settings__eyebrow {
		margin: 0;
		font-size: 12px;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--p-yellow-400);
	}

	.notification-settings__title {
		margin: 0;
		font-size: 24px;
		line-height: 1.1;
		color: var(--p-text-color);
	}

	.notification-settings__description {
		margin: 0;
		max-width: 560px;
		font-size: 14px;
		line-height: 1.6;
		color: var(--p-text-muted-color);
	}

	.notification-settings__toggle-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		padding: 16px 18px;
		border-radius: 14px;
		background: rgba(9, 9, 11, 0.45);
		border: 1px solid rgba(255, 255, 255, 0.08);

		@media (max-width: 640px) {
			flex-direction: column;
			align-items: flex-start;
		}

		.notification-settings__toggle-label {
			font-size: 15px;
			font-weight: 600;
			line-height: 1.5;
			color: var(--p-text-color);
		}
	}

	.notification-settings__error {
		margin: 0;
		color: var(--p-red-400);
	}

	.notification-settings__footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;

		@media (max-width: 640px) {
			flex-direction: column;
			align-items: stretch;
		}
	}

	.notification-settings__status {
		margin: 0;
		font-size: 13px;
		line-height: 1.6;
		color: var(--p-text-muted-color);
	}
}
</style>
